"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.StatsMenuScene = void 0;
const pixi_js_1 = require("pixi.js");
const SceneManager_1 = require("../managers/SceneManager");
const Global_1 = require("../Global");
const ui_1 = require("@pixi/ui");
const interfaces_1 = require("../../../shared/interfaces");
const blocks_1 = require("../blocks");
const i18next_1 = __importDefault(require("i18next"));
const medal_gold_svg_1 = __importDefault(require("../../assets/svg/medal_gold.svg"));
const medal_silver_svg_1 = __importDefault(require("../../assets/svg/medal_silver.svg"));
const medal_bronze_svg_1 = __importDefault(require("../../assets/svg/medal_bronze.svg"));
const medal_gold_star_svg_1 = __importDefault(require("../../assets/svg/medal_gold_star.svg"));
const gsap_1 = require("gsap");
const rxjs_1 = require("rxjs");
const BattleSprite_1 = require("../objects/BattleSprite");
const SpeedSprite_1 = require("../objects/SpeedSprite");
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
const WigglyText_1 = require("../objects/WigglyText");
const settings_1 = require("../config/settings");
const MenuScene_1 = require("./MenuScene");
const GuessSprite_1 = require("../objects/GuessSprite");
class StatsMenuScene extends MenuScene_1.MenuScene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager);
        this.curday = (0, Global_1.today)();
        this.stats = [interfaces_1.StatType.Battle, interfaces_1.StatType.Guess, interfaces_1.StatType.SpeedDay];
        this.medals = [medal_gold_svg_1.default, medal_silver_svg_1.default, medal_bronze_svg_1.default];
        this.medal = medal_gold_star_svg_1.default;
        this.battlesprite = new BattleSprite_1.BattleSprite(this.app);
        this.guesssprite = new GuessSprite_1.GuessSprite(this.app);
        this.speedsprite = new SpeedSprite_1.SpeedSprite(this.app, 8);
        this.streaksprite = pixi_js_1.Sprite.from(AssetManager_1.default.fire);
        this.zIndex = SceneManager_1.Layer.Menu;
        this.sortableChildren = true;
        this.selector = this.makeSelector();
        this.updateDayer();
        this.confetti = this.addChild(new blocks_1.Confetti(app, app.screen.width, app.screen.height, 100, 2, true));
        this.confetti.alpha = 0.38;
        this.battlesprite.zIndex = this.speedsprite.zIndex = this.guesssprite.zIndex = this.streaksprite.zIndex = SceneManager_1.Layer.Top + 1;
        this.battlesprite.scale.set(0.6);
        this.speedsprite.scale.set(0.6);
        this.guesssprite.scale.set(0.6);
        this.streaksprite.anchor.set(0.5);
        this.streaksprite.scale.set((0, Global_1.px)(0.16));
        this.speedsprite.visible = this.streaksprite.visible = this.guesssprite.visible = false;
        this.addChild(this.battlesprite, this.guesssprite, this.speedsprite, this.streaksprite);
        let last = null;
        this.subs.add(this.gameManager.stat$.pipe((0, rxjs_1.skip)(1)).subscribe((statdata) => {
            if (statdata) {
                const waslast = last === this.curstat;
                this.updateTable(this.curstat, Math.min(this.app.screen.width, (0, Global_1.px)(settings_1.Settings.maxWidth / 2)), !waslast);
                this.layout();
                last = this.curstat;
                if (!waslast) {
                    this.battlesprite.visible = this.speedsprite.visible = this.guesssprite.visible = this.streaksprite.visible = false;
                    if (this.curstat === interfaces_1.StatType.Battle) {
                        this.battlesprite.visible = true;
                        this.battlesprite.play();
                    }
                    else if (this.curstat === interfaces_1.StatType.Speed || this.curstat === interfaces_1.StatType.SpeedDay) {
                        this.speedsprite.visible = true;
                        this.speedsprite.play();
                    }
                    else if (this.curstat === interfaces_1.StatType.Streak) {
                        this.streaksprite.visible = true;
                    }
                    else if (this.curstat === interfaces_1.StatType.Guess) {
                        this.guesssprite.visible = true;
                        this.guesssprite.play();
                    }
                }
            }
        }));
    }
    updateTable(stat, width, animate = false) {
        var _a;
        if (this.table) {
            this.table.destroy({
                children: true
            });
        }
        this.table = new pixi_js_1.Container();
        const isbattle = (stat === interfaces_1.StatType.Battle);
        const data = this.gameManager.stat$.value;
        // Make Header
        const header = new pixi_js_1.Container();
        const headersize = 28;
        const rowsize = 26;
        const colpad = (0, Global_1.px)(25);
        const hstyle = blocks_1.Block.textStyle(headersize);
        const rankCol = new WigglyText_1.WigglyText(this.app, i18next_1.default.t('stats.rank'), hstyle);
        const userCol = new WigglyText_1.WigglyText(this.app, i18next_1.default.t('stats.artist'), hstyle);
        let t1, t2;
        switch (stat) {
            case interfaces_1.StatType.Battle:
            case interfaces_1.StatType.Guess:
                t1 = 'stats.won';
                t2 = 'stats.total';
                break;
            case interfaces_1.StatType.Speed:
                t1 = 'stats.won';
                t2 = 'stats.total';
                break;
            case interfaces_1.StatType.SpeedDay:
                t1 = 'stats.time';
                t2 = 'stats.inseconds';
                break;
            case interfaces_1.StatType.Streak:
                t1 = 'stats.days';
                t2 = 'stats.total';
                break;
        }
        const wstyle = blocks_1.Block.textStyle(headersize / 1.5);
        const time = new WigglyText_1.WigglyText(this.app, i18next_1.default.t(t1), hstyle);
        const time2 = new WigglyText_1.WigglyText(this.app, i18next_1.default.t(t2), wstyle);
        time2.position.set(isbattle ? (time.width / 2 - time2.width / 2) : (0, Global_1.px)(5), time.height);
        const scoreCol = new pixi_js_1.Container();
        scoreCol.addChild(time, time2);
        if ((0, Global_1.isrtl)()) {
            scoreCol.position.x = colpad / 2;
            userCol.position.x = rankCol.x + rankCol.width + colpad;
            rankCol.position.x = width - Math.max((scoreCol.width * (stat === interfaces_1.StatType.Battle || stat === interfaces_1.StatType.Guess ? 1.5 : 1)), (0, Global_1.px)(50)) - colpad / 2;
        }
        else {
            rankCol.position.x = colpad / 2;
            userCol.position.x = rankCol.x + rankCol.width + colpad;
            scoreCol.position.x = width - Math.max((scoreCol.width * (stat === interfaces_1.StatType.Battle || stat === interfaces_1.StatType.Guess ? 1.5 : 1)), (0, Global_1.px)(50)) - colpad / 2;
        }
        header.addChild(rankCol, userCol, scoreCol);
        const makeLines = (row) => {
            const size = header.children.length;
            for (let i = 1; i < size; i++) {
                const item = header.getChildAt((0, Global_1.isrtl)() ? size - 1 - i : i);
                const line = (new pixi_js_1.Graphics())
                    .lineStyle((0, Global_1.px)(1), settings_1.Settings.grayColor)
                    .moveTo(-colpad / 2, 0)
                    .lineTo(-colpad / 2, row.height);
                line.position.set(item.x, row.y);
                this.table.addChild(line);
            }
        };
        makeLines(header);
        // Make Rows
        const rstyle = blocks_1.Block.textStyle(rowsize, false);
        const makeRow = (data = null) => {
            const row = new pixi_js_1.Container();
            if (data) {
                let rank = new pixi_js_1.Text(data.s, rstyle);
                if (data.s <= 3) {
                    rank = pixi_js_1.Sprite.from(this.medals[data.s - 1]);
                    rank.scale.set((0, Global_1.px)(0.25));
                    rank.anchor.set(0.5);
                    rank.position.y = (0, Global_1.px)(8);
                }
                else {
                    rank.anchor.set(0.5, 0);
                }
                rank.position.x = rankCol.x + rankCol.width / 2;
                const ustyle = blocks_1.Block.textStyle(rowsize, data);
                ustyle.wordWrap = true;
                ustyle.wordWrapWidth = (0, Global_1.isrtl)() ? (rankCol.x - userCol.x) : (scoreCol.x - userCol.x);
                const txt = (data.u || '--') /*+ (data.pkey === this.gameManager.playerManager.pkey ? ' (you)' : '') */;
                const user = new pixi_js_1.Text(txt, ustyle);
                user.position.x = userCol.x;
                let scr = '-';
                if (data.score && !isNaN(data.score)) {
                    if (stat === interfaces_1.StatType.SpeedDay) {
                        scr = (data.score / 1000000).toFixed(2);
                    }
                    else {
                        scr = data.score.toString();
                    }
                }
                const score = new pixi_js_1.Text(scr, rstyle);
                score.anchor.set(0.5, 0);
                score.position.x = scoreCol.x + scoreCol.width / 2;
                // Add medal icon
                if (stat === interfaces_1.StatType.Battle || stat === interfaces_1.StatType.Guess) {
                    const medal = pixi_js_1.Sprite.from(this.medal);
                    medal.scale.set((0, Global_1.px)(0.21));
                    if ((0, Global_1.isrtl)()) {
                        medal.anchor.set(1, 0.4);
                        medal.position.set((0, Global_1.px)(-20), (0, Global_1.px)(8));
                    }
                    else {
                        medal.anchor.set(0, 0.4);
                        medal.position.set((0, Global_1.px)(20), (0, Global_1.px)(8));
                    }
                    score.addChild(medal);
                }
                row.addChild(rank, user, score);
            }
            else {
                // ...
                const estyle = blocks_1.Block.textStyle(48, false);
                estyle.fill = settings_1.Settings.grayColor;
                const dots = new pixi_js_1.Text('…', estyle);
                dots.anchor.set(0.5, -.15);
                dots.position.x = width / 2;
                row.addChild(dots);
            }
            return row;
        };
        this.table.zIndex = SceneManager_1.Layer.Bottom;
        this.table.addChild(header);
        let lastrow = header;
        const rows = [];
        for (let i = 0; i < data.length; i++) {
            if (i && data[i].s !== data[i - 1].s && data[i].s !== data[i - 1].s + 1) {
                // Add ...
                const row = makeRow();
                row.position.y = lastrow.y + (this.isLandscapeBox ? 0 : lastrow.height / 2);
                this.table.addChild(row);
                lastrow = row;
            }
            const myrow = makeRow(data[i]);
            myrow.position.y = lastrow.y + lastrow.height;
            makeLines(myrow);
            this.table.addChild(myrow);
            lastrow = myrow;
            rows.push(myrow);
        }
        if (animate) {
            (_a = this.tm) === null || _a === void 0 ? void 0 : _a.kill();
            this.tm = gsap_1.gsap.timeline({ paused: true });
            rows.forEach((r, i) => {
                r.alpha = 1;
                this.tm.from(r, {
                    pixi: {
                        alpha: 0,
                        positionY: this.app.screen.height
                    },
                    duration: 0.35,
                    delay: i * 0.03
                }, '<');
            });
            this.tm.play(0);
        }
        return this.addChild(this.table);
    }
    updateDayer() {
        const istoday = this.curday.getTime() === (0, Global_1.today)().getTime();
        const date = () => {
            const year = this.curday.getUTCFullYear();
            const month = i18next_1.default.t('months.' + (this.curday.getMonth() + 1));
            const day = this.curday.getUTCDate();
            let txt = month + ' ' + day + (year === (0, Global_1.today)().getUTCFullYear() ? '' : ', ' + year);
            const yesterday = (0, Global_1.today)();
            yesterday.setUTCDate(yesterday.getUTCDate() - 1);
            if (istoday) {
                txt = i18next_1.default.t('today');
            }
            else if (this.curday.getTime() === yesterday.getTime()) {
                txt = i18next_1.default.t('yesterday');
            }
            return txt;
        };
        if (!this.dayer) {
            this.dayer = new pixi_js_1.Container();
            const arrowstyle = new pixi_js_1.TextStyle({
                fontWeight: 'lighter',
                fontFamily: 'Arial',
                fontSize: (0, Global_1.px)(40)
            });
            const text = new pixi_js_1.Text(date(), blocks_1.Block.textStyle());
            text.anchor.set(0.4, 0);
            const leftText = new pixi_js_1.Text(' › ', arrowstyle);
            leftText.scale.x = -1;
            leftText.anchor.set(.8, 0.25);
            const left = new ui_1.ButtonContainer(leftText);
            this.sigs.push(left.onPress.connect(() => {
                this.moveDay(-1);
                AssetManager_1.default.tickSound();
            }));
            const rightText = new pixi_js_1.Text(' › ', arrowstyle);
            rightText.anchor.set(.35, 0.25);
            const right = new ui_1.ButtonContainer(rightText);
            right.visible = false;
            this.sigs.push(right.onPress.connect(() => {
                this.moveDay(1);
                AssetManager_1.default.tickSound();
            }));
            this.dayer.addChild(text, left, right);
            this.addChild(this.dayer);
        }
        else {
            (this.dayer.getChildAt(0)).text = date();
        }
        (this.dayer.getChildAt(2)).visible = this.curday.getTime() < (0, Global_1.today)().getTime();
        this.dayer.visible = this.curstat === interfaces_1.StatType.SpeedDay;
    }
    moveDay(delta = -1) {
        this.curday.setUTCDate(this.curday.getUTCDate() + delta);
        this.updateDayer();
        this.layout();
        this.update();
    }
    makeSelector() {
        const scale = 32;
        const clearbg = 'rgba(255, 255, 255, 0.0001)';
        const arrow = new pixi_js_1.Graphics();
        const drawarrow = () => {
            this.makeBox({
                backgroundColor: this.curstat === interfaces_1.StatType.Battle ? settings_1.Settings.battleColor : (this.curstat === interfaces_1.StatType.Guess ? settings_1.Settings.guessColor : settings_1.Settings.speedColor),
                width: scale,
                height: scale,
                border: 2,
                radius: 1
            }, arrow);
        };
        drawarrow();
        const arrowstyle = new pixi_js_1.TextStyle({
            fontWeight: 'lighter',
            fontFamily: 'Arial',
            fontSize: (0, Global_1.px)(scale + 6)
        });
        const arrowtext = new pixi_js_1.Text('›', arrowstyle);
        arrowtext.anchor.set(0.5);
        arrowtext.rotation = Math.PI / 2;
        arrowtext.position.set(arrow.width / 2 + (0, Global_1.px)(3), arrow.height / 2);
        arrow.addChild(arrowtext);
        const invertarrow = arrow.clone();
        const invertarrowtext = new pixi_js_1.Text('›', arrowstyle);
        invertarrowtext.anchor.set(0.5);
        invertarrowtext.rotation = -Math.PI / 2;
        invertarrowtext.position.set(invertarrow.width / 2 - (0, Global_1.px)(3), invertarrow.height / 2);
        invertarrow.addChild(invertarrowtext);
        // Trick to make it look "transparent"
        const closed = this.makeBox({
            backgroundColor: settings_1.Settings.whiteColor,
            border: 0,
            height: scale + 6,
            width: 200
        });
        arrow.position.set(closed.width - arrow.width, closed.height / 2 - arrow.height / 2);
        closed.addChild(arrow);
        const open = this.makeBox({
            height: (closed.height / (0, Global_1.px)() + 6) * (this.stats.length),
            backgroundColor: settings_1.Settings.whiteColor,
            width: 200
        });
        const offset = new pixi_js_1.Container();
        open.position.y = (0, Global_1.px)(scale + 8);
        open.position.x = (0, Global_1.px)(-2);
        offset.addChild(open);
        invertarrow.position.set(closed.width - invertarrow.width, closed.height / 2 - invertarrow.height / 2);
        offset.addChild(invertarrow);
        const hstyle = blocks_1.Block.textStyle(scale + 6);
        hstyle.align = 'left';
        const bstyle = blocks_1.Block.textStyle(scale);
        bstyle.align = 'left';
        const select = new ui_1.Select({
            closedBG: closed,
            openBG: offset,
            textStyle: hstyle,
            items: {
                items: this.stats.map((s) => i18next_1.default.t('stats.' + s.toString())),
                backgroundColor: clearbg,
                radius: 0,
                textStyle: bstyle,
                width: open.width - (0, Global_1.px)(4),
                height: closed.height,
                hoverColor: settings_1.Settings.lightLightGrayColor
            }
        });
        this.sigs.push(select.onSelect.connect(() => {
            this.update();
            drawarrow();
            AssetManager_1.default.tickSound();
        }));
        select.zIndex = SceneManager_1.Layer.Top;
        return this.addChild(select);
    }
    update() {
        const stat = this.curstat;
        if (stat) {
            if (stat !== interfaces_1.StatType.SpeedDay) {
                this.curday = (0, Global_1.today)();
            }
            this.updateDayer();
            this.gameManager.checkStat(stat, Math.floor(this.curday.getTime() / 1000));
        }
    }
    get curstat() {
        var _a;
        return this.stats[((_a = this.selector) === null || _a === void 0 ? void 0 : _a.value) || 0];
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.init.call(this);
            this.update();
        });
    }
    destroy(_options) {
        var _a;
        (_a = this.tm) === null || _a === void 0 ? void 0 : _a.kill();
        this.tm = null;
        super.destroy(_options);
    }
    get triggersStop() {
        return true;
    }
    layout() {
        var _a, _b, _c, _d, _e, _f;
        super.layout();
        const center = this.center.x;
        const w = this.selector.width;
        let l;
        if (this.isLandscapeBox) {
            l = center - w + (0, Global_1.px)(24);
            this.selector.position.set(l, (0, Global_1.px)(10));
            (_a = this.dayer) === null || _a === void 0 ? void 0 : _a.position.set(center + w / 2 + (0, Global_1.px)(10), this.selector.y + (0, Global_1.px)(5));
        }
        else {
            l = center - w / 2;
            this.selector.position.set(l, (0, Global_1.px)(this.overSmallSize ? 100 : 80));
            (_b = this.dayer) === null || _b === void 0 ? void 0 : _b.position.set(center, this.selector.y + this.selector.height + (0, Global_1.px)(5));
        }
        this.battlesprite.position.set(l + (0, Global_1.px)(24), this.selector.y - (0, Global_1.px)(11));
        this.speedsprite.position.set(l + (0, Global_1.px)(24), this.selector.y + (0, Global_1.px)(16));
        this.streaksprite.position.set(l + (0, Global_1.px)(24), this.selector.y + (0, Global_1.px)(11));
        this.guesssprite.position.set(l + (0, Global_1.px)(24), this.selector.y + (0, Global_1.px)(11));
        (_c = this.table) === null || _c === void 0 ? void 0 : _c.position.set(this.center.x - this.table.width / 2 - (0, Global_1.px)(8), (0, Global_1.px)(this.isLandscapeBox ? 60 : (this.overSmallSize ? 220 : 180)));
        const left = ((_d = this.dayer) === null || _d === void 0 ? void 0 : _d.getChildAt(1));
        const right = ((_e = this.dayer) === null || _e === void 0 ? void 0 : _e.getChildAt(2));
        const text = ((_f = this.dayer) === null || _f === void 0 ? void 0 : _f.getChildAt(0));
        if (text && left && right) {
            left.x = -text.width / 2 - (0, Global_1.px)(25);
            right.x = text.width / 2 + (0, Global_1.px)(25);
        }
    }
}
exports.StatsMenuScene = StatsMenuScene;
